import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

/* ── DAG node positions (relative to DAG container 860×480) ── */
const DAG_W = 860;
const DAG_H = 480;

const NODE_TASK_PARSER = { cx: DAG_W / 2, cy: 50, label: "Task Parser", color: COLORS.neonPurple };
const NODE_GEN        = { cx: DAG_W * 0.18, cy: 170, label: "Generate Report", color: COLORS.neonGreen };
const NODE_SEND       = { cx: DAG_W * 0.50, cy: 170, label: "Send ×3", color: COLORS.neonBlue };
const NODE_WATCH      = { cx: DAG_W * 0.82, cy: 170, label: "Watch Triggers", color: COLORS.neonOrange };
const NODE_WAIT       = { cx: DAG_W * 0.50, cy: 290, label: "Wait for responses", color: COLORS.neonCyan };
const LEAF_NODES = [
  { cx: DAG_W * 0.12, cy: 400, label: "Sentiment", color: COLORS.neonPink },
  { cx: DAG_W * 0.37, cy: 400, label: "Insights",  color: COLORS.neonYellow },
  { cx: DAG_W * 0.63, cy: 400, label: "Tasks",     color: COLORS.neonGreen },
  { cx: DAG_W * 0.88, cy: 400, label: "Notify",    color: COLORS.neonOrange },
];

function glow(color: string, strong = false): string {
  return strong
    ? `0 0 20px ${color}99, 0 0 50px ${color}50, 0 0 80px ${color}20`
    : `0 0 15px ${color}60, 0 0 35px ${color}25`;
}

/* ── Animated SVG connector line ── */
const Connector: React.FC<{
  x1: number; y1: number; x2: number; y2: number;
  color: string; frame: number; delay: number; fps: number;
}> = ({ x1, y1, x2, y2, color, frame, delay, fps }) => {
  const progress = spring({ frame: frame - delay, fps, config: SPRING.gentle });
  const opacity  = interpolate(progress, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const len = Math.hypot(x2 - x1, y2 - y1);
  const dashOffset = interpolate(frame % 60, [0, 60], [len, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color}
      strokeWidth={2.5}
      strokeDasharray={`${len * 0.3} ${len * 0.7}`}
      strokeDashoffset={dashOffset}
      opacity={opacity}
      style={{ filter: `drop-shadow(0 0 6px ${color}99)` }}
    />
  );
};

/* ── Hexagonal node ── */
const DagNode: React.FC<{
  cx: number; cy: number; label: string; color: string;
  frame: number; delay: number; fps: number; size?: number;
}> = ({ cx, cy, label, color, frame, delay, fps, size = 44 }) => {
  const sp = spring({ frame: frame - delay, fps, config: SPRING.bouncy });
  const scale = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const opacity = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pulse = 1 + Math.sin(frame * 0.08) * 0.04;

  return (
    <g transform={`translate(${cx}, ${cy})`} opacity={opacity} style={{ transformOrigin: `${cx}px ${cy}px` }}>
      <circle
        r={size * scale * pulse}
        fill={`${color}18`}
        stroke={color}
        strokeWidth={2.5}
        style={{ filter: `drop-shadow(0 0 12px ${color}80)` }}
      />
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={size > 40 ? 11 : 10}
        fontFamily={FONTS.mono}
        fontWeight={700}
        fill={color}
        y={0}
      >
        {label}
      </text>
    </g>
  );
};

/* ── Particle flowing along line ── */
const FlowParticle: React.FC<{
  x1: number; y1: number; x2: number; y2: number;
  color: string; frame: number; offset: number;
}> = ({ x1, y1, x2, y2, color, frame, offset }) => {
  const t = ((frame * 0.015 + offset) % 1);
  const px = x1 + (x2 - x1) * t;
  const py = y1 + (y2 - y1) * t;
  return (
    <circle cx={px} cy={py} r={4} fill={color}
      style={{ filter: `drop-shadow(0 0 8px ${color})` }} />
  );
};

export const Scene09Phase4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  /* Exit fade */
  const fadeOut = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Title */
  const titleSp = spring({ frame, fps, config: SPRING.snappy });
  const titleY  = interpolate(titleSp, [0, 1], [-40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  /* Instruction box */
  const instrSp  = spring({ frame: frame - 15, fps, config: SPRING.gentle });
  const instrOp  = interpolate(instrSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const typingChars = Math.floor(
    interpolate(frame, [20, 110], [0, 68], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
  );
  const fullText = "Create market report → Send to 3 clients → Monitor KB → Auto-update → Track sentiment → Flag actions";

  /* Badge stagger */
  const badges = [
    { label: "on_ingest", color: COLORS.neonPink },
    { label: "on_response", color: COLORS.neonCyan },
    { label: "on_schedule", color: COLORS.neonYellow },
    { label: "on_threshold", color: COLORS.neonOrange },
  ];

  /* Background radial glow */
  const bgGlow: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    background: `radial-gradient(ellipse 70% 60% at 50% 40%, ${COLORS.neonPurple}08 0%, transparent 70%)`,
  };

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut }}>
      {/* Background glow */}
      <div style={bgGlow} />

      {/* Subtle grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${COLORS.neonPurple}08 1px, transparent 1px), linear-gradient(90deg, ${COLORS.neonPurple}08 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonPurple, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonCyan, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonBlue, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div style={{
        position: "absolute",
        top: 40,
        left: 80,
        transform: `translateY(${titleY}px)`,
        opacity: interpolate(titleSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
      }}>
        <div style={{
          fontSize: 44,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          color: COLORS.neonPurple,
          textShadow: glow(COLORS.neonPurple, true),
          letterSpacing: -1,
        }}>
          Phase 4: Autonomous Chains
        </div>
      </div>

      {/* Instruction box */}
      <div style={{
        position: "absolute",
        top: 110,
        left: "50%",
        transform: "translateX(-50%) perspective(600px) rotateY(-3deg)",
        width: 1100,
        opacity: instrOp,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.neonPurple}15, ${COLORS.bgCard})`,
          border: `1.5px solid ${COLORS.neonPurple}60`,
          borderRadius: 12,
          padding: "14px 24px",
          boxShadow: `0 0 30px ${COLORS.neonPurple}30, 0 0 60px ${COLORS.neonPurple}10, inset 0 0 30px ${COLORS.neonPurple}08`,
        }}>
          <div style={{
            fontFamily: FONTS.mono,
            fontSize: 18,
            color: COLORS.neonCyan,
            letterSpacing: 0.5,
            minHeight: 26,
          }}>
            {fullText.slice(0, typingChars)}
            {typingChars < fullText.length && (
              <span style={{
                display: "inline-block",
                width: 2,
                height: 20,
                background: COLORS.neonCyan,
                marginLeft: 2,
                opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0,
                verticalAlign: "middle",
              }} />
            )}
          </div>
        </div>
      </div>

      {/* DAG container with 3D perspective */}
      <div style={{
        position: "absolute",
        top: 200,
        left: "50%",
        transform: `translateX(-50%) perspective(1000px) rotateX(8deg)`,
        width: DAG_W,
        height: DAG_H,
      }}>
        <svg width={DAG_W} height={DAG_H} style={{ overflow: "visible" }}>
          {/* Connectors: Task Parser → Step nodes */}
          {[NODE_GEN, NODE_SEND, NODE_WATCH].map((n, i) => (
            <Connector key={`tp-${i}`}
              x1={NODE_TASK_PARSER.cx} y1={NODE_TASK_PARSER.cy + 44}
              x2={n.cx} y2={n.cy - 44}
              color={n.color} frame={frame} delay={70 + i * 8} fps={fps}
            />
          ))}

          {/* Send → Wait */}
          <Connector
            x1={NODE_SEND.cx} y1={NODE_SEND.cy + 44}
            x2={NODE_WAIT.cx} y2={NODE_WAIT.cy - 44}
            color={COLORS.neonCyan} frame={frame} delay={110} fps={fps}
          />

          {/* Wait → Leaf nodes */}
          {LEAF_NODES.map((n, i) => (
            <Connector key={`wait-${i}`}
              x1={NODE_WAIT.cx} y1={NODE_WAIT.cy + 44}
              x2={n.cx} y2={n.cy - 32}
              color={n.color} frame={frame} delay={140 + i * 8} fps={fps}
            />
          ))}

          {/* Loop arrow from bottom back to Wait */}
          {(() => {
            const loopSp = spring({ frame: frame - 190, fps, config: SPRING.gentle });
            const loopOp = interpolate(loopSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const dashOffset = interpolate(frame % 80, [0, 80], [200, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            return (
              <g opacity={loopOp}>
                <path
                  d={`M ${LEAF_NODES[3].cx + 32} ${LEAF_NODES[3].cy}
                      C ${DAG_W - 20} ${LEAF_NODES[3].cy},
                        ${DAG_W - 20} ${NODE_WAIT.cy},
                        ${NODE_WAIT.cx + 50} ${NODE_WAIT.cy}`}
                  fill="none"
                  stroke={COLORS.neonPurple}
                  strokeWidth={2}
                  strokeDasharray="10 8"
                  strokeDashoffset={dashOffset}
                  style={{ filter: `drop-shadow(0 0 6px ${COLORS.neonPurple}80)` }}
                />
                <polygon
                  points={`${NODE_WAIT.cx + 50},${NODE_WAIT.cy - 6} ${NODE_WAIT.cx + 64},${NODE_WAIT.cy} ${NODE_WAIT.cx + 50},${NODE_WAIT.cy + 6}`}
                  fill={COLORS.neonPurple}
                  opacity={loopOp}
                />
                <text
                  x={DAG_W - 10} y={(LEAF_NODES[3].cy + NODE_WAIT.cy) / 2}
                  fontSize={10} fontFamily={FONTS.mono} fill={COLORS.neonPurple}
                  textAnchor="middle" dominantBaseline="middle"
                >loop</text>
              </g>
            );
          })()}

          {/* Flow particles */}
          {frame > 80 && [NODE_GEN, NODE_SEND, NODE_WATCH].map((n, i) => (
            <FlowParticle key={`fp-${i}`}
              x1={NODE_TASK_PARSER.cx} y1={NODE_TASK_PARSER.cy + 44}
              x2={n.cx} y2={n.cy - 44}
              color={n.color} frame={frame} offset={i * 0.33}
            />
          ))}
          {frame > 130 && (
            <FlowParticle
              x1={NODE_SEND.cx} y1={NODE_SEND.cy + 44}
              x2={NODE_WAIT.cx} y2={NODE_WAIT.cy - 44}
              color={COLORS.neonCyan} frame={frame} offset={0.5}
            />
          )}
          {frame > 150 && LEAF_NODES.map((n, i) => (
            <FlowParticle key={`lp-${i}`}
              x1={NODE_WAIT.cx} y1={NODE_WAIT.cy + 44}
              x2={n.cx} y2={n.cy - 32}
              color={n.color} frame={frame} offset={i * 0.25}
            />
          ))}

          {/* DAG Nodes */}
          <DagNode {...NODE_TASK_PARSER} frame={frame} delay={50} fps={fps} size={50} />
          <DagNode {...NODE_GEN}  frame={frame} delay={80}  fps={fps} />
          <DagNode {...NODE_SEND} frame={frame} delay={88}  fps={fps} />
          <DagNode {...NODE_WATCH} frame={frame} delay={96} fps={fps} />
          <DagNode {...NODE_WAIT} frame={frame} delay={130} fps={fps} size={50} />
          {LEAF_NODES.map((n, i) => (
            <DagNode key={`leaf-${i}`} {...n} frame={frame} delay={150 + i * 10} fps={fps} size={36} />
          ))}
        </svg>
      </div>

      {/* Event trigger badges */}
      <div style={{
        position: "absolute",
        bottom: 60,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: 20,
        alignItems: "center",
      }}>
        <div style={{
          fontSize: 13,
          fontFamily: FONTS.mono,
          color: COLORS.textMuted,
          marginRight: 8,
          letterSpacing: 1,
        }}>
          EVENT TRIGGERS:
        </div>
        {badges.map((b, i) => {
          const sp = spring({ frame: frame - (220 + i * 12), fps, config: SPRING.punch });
          const scale = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={b.label} style={{
              transform: `scale(${scale})`,
              opacity: op,
              padding: "7px 18px",
              borderRadius: 20,
              background: `${b.color}18`,
              border: `1.5px solid ${b.color}80`,
              fontFamily: FONTS.mono,
              fontSize: 14,
              fontWeight: 700,
              color: b.color,
              boxShadow: glow(b.color),
              letterSpacing: 0.5,
            }}>
              {b.label}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
